import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api';
import './UserModMenu.css';

const DEFAULT_TIMEOUT_SECONDS = 300;

function UserModMenu({
  user,
  lobbyId,
  moderation,
  anchorRect,
  tableContext,
  onTableKick,
  onClose,
  onActionComplete,
}) {
  const menuRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showTimeout, setShowTimeout] = useState(false);
  const [timeoutMinutes, setTimeoutMinutes] = useState(5);
  const [reason, setReason] = useState('');

  useEffect(() => {
    const onPointerDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  if (!user || (!moderation?.can_moderate_lobby && !moderation?.can_moderate_global)) {
    return null;
  }

  const style = anchorRect
    ? {
      position: 'fixed',
      top: Math.min(anchorRect.bottom + 4, window.innerHeight - 280),
      left: Math.min(anchorRect.left, window.innerWidth - 220),
      zIndex: 1200,
    }
    : undefined;

  const runAction = async (path, body) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.message || 'Action failed');
      onActionComplete?.(data.message);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const baseBody = {
    targetUserId: user.userId,
    reason: reason.trim() || undefined,
    lobbyId: Number(lobbyId),
  };

  const tableKickAction = tableContext && moderation.lobby_kick && onTableKick && {
    key: 'table-kick',
    label: 'Remove from table',
    onClick: () => {
      onTableKick(user.userId);
      onClose();
    },
  };

  const lobbyActions = [
    tableKickAction,
    !tableContext && moderation.lobby_kick && {
      key: 'kick',
      label: 'Kick (5 min)',
      onClick: () => runAction(`/moderation/lobby/${lobbyId}/kick`, baseBody),
    },
    moderation.lobby_timeout && {
      key: 'timeout',
      label: 'Time out…',
      onClick: () => setShowTimeout(true),
    },
    moderation.lobby_mute && {
      key: 'mute',
      label: 'Mute (1 hr)',
      onClick: () => runAction(`/moderation/lobby/${lobbyId}/mute`, {
        ...baseBody,
        durationSeconds: 3600,
      }),
    },
    moderation.lobby_ban && {
      key: 'ban',
      label: 'Ban from lobby',
      onClick: () => runAction(`/moderation/lobby/${lobbyId}/ban`, baseBody),
    },
    moderation.lobby_unban && {
      key: 'unban',
      label: 'Unban from lobby',
      onClick: () => runAction(`/moderation/lobby/${lobbyId}/unban`, baseBody),
    },
  ].filter(Boolean);

  const globalActions = [
    moderation.global_kick && {
      key: 'global-kick',
      label: 'Global kick',
      onClick: () => runAction('/moderation/global/kick', baseBody),
    },
    moderation.global_mute && {
      key: 'global-mute',
      label: 'Global mute',
      onClick: () => runAction('/moderation/global/mute', {
        targetUserId: user.userId,
        reason: reason.trim() || undefined,
        durationSeconds: 3600,
      }),
    },
    moderation.global_ban && {
      key: 'global-ban',
      label: 'Global ban',
      onClick: () => runAction('/moderation/global/ban', baseBody),
    },
  ].filter(Boolean);

  return (
    <div ref={menuRef} className="user-mod-menu" style={style} role="menu">
      <div className="user-mod-menu-head">
        <strong>{user.username}</strong>
      </div>

      <label className="user-mod-field">
        <span>Reason (optional)</span>
        <input
          type="text"
          maxLength={200}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
        />
      </label>

      {showTimeout ? (
        <div className="user-mod-timeout">
          <label className="user-mod-field">
            <span>Minutes</span>
            <input
              type="number"
              min={1}
              max={1440}
              value={timeoutMinutes}
              onChange={(e) => setTimeoutMinutes(Math.max(1, parseInt(e.target.value, 10) || 1))}
              disabled={busy}
            />
          </label>
          <button
            type="button"
            className="user-mod-btn"
            disabled={busy}
            onClick={() => runAction(`/moderation/lobby/${lobbyId}/timeout`, {
              ...baseBody,
              durationSeconds: Math.max(30, timeoutMinutes * 60) || DEFAULT_TIMEOUT_SECONDS,
            })}
          >
            Apply timeout
          </button>
          <button type="button" className="user-mod-btn user-mod-btn--ghost" onClick={() => setShowTimeout(false)}>
            Back
          </button>
        </div>
      ) : (
        <>
          {lobbyActions.length > 0 && (
            <div className="user-mod-group">
              <span className="user-mod-group-label">Lobby</span>
              {lobbyActions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="user-mod-btn"
                  disabled={busy}
                  onClick={action.onClick}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
          {globalActions.length > 0 && (
            <div className="user-mod-group">
              <span className="user-mod-group-label">Global</span>
              {globalActions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="user-mod-btn user-mod-btn--danger"
                  disabled={busy}
                  onClick={action.onClick}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {error && <p className="user-mod-error">{error}</p>}
    </div>
  );
}

export default UserModMenu;